import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { comCorpo, pedir } from '@/lib/pedido';
import { useSessao } from '@/modulos/sessao/consultas';

import { Contexto, type ConferenciaDaHistoria, type ValorDoModo } from './contexto';
import { historiaDaAncora } from './roteiro';

/** O provedor do modo histórias: o interruptor no aparelho e o estado do servidor (ver `contexto.ts`). */
const CHAVE = 'ct-frotas:modo-historias';

function lerDoAparelho(): boolean {
  try {
    return window.localStorage.getItem(CHAVE) === '1';
  } catch {
    return false;
  }
}

function gravarNoAparelho(ligado: boolean): void {
  try {
    window.localStorage.setItem(CHAVE, ligado ? '1' : '0');
  } catch {
    /* Sem armazenamento (janela privada): o modo vale até recarregar. */
  }
}

export function ProvedorDeHistorias({ children }: { readonly children: ReactNode }) {
  const sessao = useSessao();
  const cliente = useQueryClient();
  const [ligadoNoAparelho, setLigado] = useState(lerDoAparelho);
  const [naTela, setNaTela] = useState<readonly string[]>([]);
  const [aberta, setAberta] = useState<string | null>(null);

  const estado = useQuery({
    queryKey: ['historias', 'estado'],
    enabled: sessao.data !== undefined && sessao.data !== null,
    retry: false,
    staleTime: 60_000,
    meta: { silenciarFalha: true },
    queryFn: () => pedir<{ homologacao: boolean; conferencias: readonly ConferenciaDaHistoria[] }>('/historias/estado'),
  });
  const homologacao = estado.data?.homologacao ?? false;
  const ativo = homologacao && ligadoNoAparelho;

  const conferir = useMutation({
    mutationFn: (p: { codigo: string; situacao: 'conferida' | 'divergente'; nota?: string; paraPapelId?: string }) =>
      comCorpo<{ codigo: string; destino: string | null }>('POST', `/historias/${p.codigo}/conferencia`, {
        situacao: p.situacao,
        ...(p.nota ? { nota: p.nota } : {}),
        ...(p.paraPapelId ? { paraPapelId: p.paraPapelId } : {}),
        /* A tela, o filtro e o endereço (p.48): o responsável abre o mesmo lugar. */
        endereco: `${window.location.pathname}${window.location.search}`.slice(0, 500),
      }),
    meta: { silencioso: true },
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['historias', 'estado'] });
    },
  });

  const alternar = useCallback(() => {
    setLigado((l) => {
      gravarNoAparelho(!l);
      return !l;
    });
  }, []);

  /* O link da história ("Copiar link", p.47): em homologação, liga o modo e
     abre o cartão daquele código, na tela e com o filtro de quem copiou. */
  useEffect(() => {
    if (!homologacao) return;
    const codigo = historiaDaAncora(window.location.hash);
    if (!codigo) return;
    setLigado(true);
    gravarNoAparelho(true);
    setAberta(codigo);
  }, [homologacao]);

  /* Atalho Ctrl ⇧ H (p.48), só em homologação. Esc fecha o cartão pelo componente dele. */
  useEffect(() => {
    if (!homologacao) return undefined;
    const tecla = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && (e.key === 'H' || e.key === 'h')) {
        e.preventDefault();
        alternar();
      }
    };
    window.addEventListener('keydown', tecla);
    return () => {
      window.removeEventListener('keydown', tecla);
    };
  }, [homologacao, alternar]);

  /* Um elemento pode aparecer mais de uma vez (uma ação por linha): o código
     sai da tela só quando a ÚLTIMA cópia desmonta. */
  const copias = useRef(new Map<string, number>());
  const registrar = useCallback((codigo: string) => {
    copias.current.set(codigo, (copias.current.get(codigo) ?? 0) + 1);
    setNaTela((lista) => (lista.includes(codigo) ? lista : [...lista, codigo]));
    return () => {
      const resta = (copias.current.get(codigo) ?? 1) - 1;
      if (resta > 0) {
        copias.current.set(codigo, resta);
        return;
      }
      copias.current.delete(codigo);
      setNaTela((lista) => lista.filter((c) => c !== codigo));
    };
  }, []);

  const valor = useMemo<ValorDoModo>(
    () => ({
      homologacao,
      ativo,
      ligadoNoAparelho,
      alternar,
      conferencias: new Map((estado.data?.conferencias ?? []).map((c) => [c.codigo, c])),
      naTela,
      registrar,
      aberta: ativo ? aberta : null,
      abrir: setAberta,
      fechar: () => {
        setAberta(null);
      },
      conferir: async (codigo, situacao, nota, paraPapelId) => {
        const r = await conferir.mutateAsync({ codigo, situacao, ...(nota ? { nota } : {}), ...(paraPapelId ? { paraPapelId } : {}) });
        return r.destino;
      },
      conferindo: conferir.isPending,
    }),
    [homologacao, ativo, ligadoNoAparelho, alternar, estado.data, naTela, registrar, aberta, conferir],
  );
  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

